<template>
  <v-dialog
    v-model="dialog"
    max-width="80%"
    max-height="80%"
    :style="{ zIndex: 200 }"
    @keydown.esc="cancel"
    @click:outside="cancel"
  >
    <v-card class="dialog-contents">
      <v-app-bar
        absolute
        color="primary"
        ref="toolbar"
        scroll-target="#scrolling-viewer"
        class="viewer__header"
        id="header-Viewer"
      >
        <v-toolbar-title class="white--text">
          <template v-if="file_name">
            {{ file_name }}&nbsp;({{ fid }})
          </template>
          <template v-else>
            {{ $t("message.common.no_id", { id: fid }) }}
          </template>
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <template v-if="extension === 'json'">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" class="ma-2" @click.native="get_excel" :disabled="data.length == 0" dark>
                  mdi-file-excel
                </v-icon>
              </template>
              <span>{{ $t("message.common.get_excel") }}</span>
            </v-tooltip>
          </template>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon color="white" v-on="on" class="ma-2" @click.native="cancel">
                mdi-close
              </v-icon>
            </template>
            <span>{{ $t("message.common.close") }}</span>
          </v-tooltip>
        </v-toolbar-items>
        <template v-if="extension === 'json'" v-slot:extension>
          <v-toolbar-items>
            <v-card class="d-flex justify-center search-area" width="100%">
              <v-card flat class="pa-2">
                <v-text-field
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  :label="$t('message.common.source')"
                  v-model="search_source"
                >
                </v-text-field>
              </v-card>
              <v-card flat class="pa-2">
                <v-text-field
                  prepend-inner-icon="mdi-magnify"
                  clearable
                  :label="$t('message.viewer.translated')"
                  v-model="search_translated"
                >
                </v-text-field>
              </v-card>
            </v-card>
          </v-toolbar-items>
        </template>
      </v-app-bar>
      <v-card
        v-show="extension === 'txt'"
        width="100%"
        :height="viewer_height"
        class="pa-3 pre-line bidi"
      >
        {{ get_text }}
      </v-card>
      <v-sheet
        v-show="extension === 'json'"
        id="scrolling-viewer"
        class="overflow-y-auto"
        :max-height="sheetHeight"
      >
        <v-data-table
          :headers="headers"
          :items="data"
          class="elevation-1"
          :page.sync="page"
          :options.sync="options"
          must-sort
          :footer-props="{
            'items-per-page-options': [10, 100, 200, 500]
          }"
        >
          <template v-slot:item.index="{ item }">
            {{ Array.isArray(item.index) ? item.index.join("-") : item.index }}
          </template>
          <template v-slot:item.source="{ item }">
            <div class="bidi">
              <template v-if="item.source !== ''">
                {{ item.source }}
              </template>
              <template v-else>
                -
              </template>
            </div>
          </template>
          <template v-slot:item.translated="{ item }">
            <div class="bidi">
              <template v-if="item.translated !== ''">
                {{ item.translated }}
              </template>
              <template v-else>
                -
              </template>
            </div>
          </template>
          <template v-slot:item.count="{ item }">
            {{ item.count }}
          </template>
          <template v-slot:item.score_machine_translate="{ item }">
            {{ typeof item.score_machine_translate === 'number' ? item.score_machine_translate.toFixed(1) : '-' }}
          </template>
          <template v-slot:item.score_custom_model="{ item }">
            {{ typeof item.score_custom_model === 'number' ? item.score_custom_model.toFixed(1) : '-' }}
          </template>
        </v-data-table>
      </v-sheet>
      <div
        v-show="extension === 'txt'"
        class="d-flex flex-row-reverse"
        ref="copyButton"
      >
        <v-btn
          v-show="extension === 'txt'"
          color="primary"
          class="my-2 mr-3"
          @click="copy_text"
        >{{ $t("message.viewer.copy") }}</v-btn>
      </div>
      <v-card v-show="['txt', 'json'].indexOf(extension) < 0">
        <div
          v-show="['pdf', 'ocr'].indexOf(extension) >= 0"
          :style="{ width: viewer_width + 'px', height: viewer_height + 'px' }"
          class="no-radius canvas-contents"
        >
          <canvas ref="pdf_canvas" />
        </div>
        <div
          v-show="['pdf', 'ocr'].indexOf(extension) === -1"
          ref="viewerContents"
          :style="{ width: viewer_width + 1 + 'px', height: viewer_height + 'px' }"
          class="no-radius"
        >
        </div>
        <v-card>
          <p class="text-center mb-0">
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon
                  v-on="on"
                  class="ma-2"
                  @click.native="prev"
                  :disabled="extension === 'docx' || (extension !== 'docx' && currentId === 0)"
                >
                  mdi-skip-previous
                </v-icon>
              </template>
              <span>{{ $t("message.common.prev") }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon
                  v-on="on"
                  class="ma-2"
                  @click.native="next"
                  :disabled="extension === 'docx' || (extension !== 'docx' && currentId >= (total - 1))"
                >
                  mdi-skip-next
                </v-icon>
              </template>
              <span>{{ $t("message.common.next") }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" class="ma-2" @click.native="zoom_in">
                  mdi-magnify-plus
                </v-icon>
              </template>
              <span>{{ $t("message.viewer.zoom_in") }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" class="ma-2" @click.native="zoom_out">
                  mdi-magnify-minus
                </v-icon>
              </template>
              <span>{{ $t("message.viewer.zoom_out") }}</span>
            </v-tooltip>
            <template v-if="extension !== 'docx'">
              ({{ currentId + 1 }}/{{ total }})
            </template>
          </p>
        </v-card>
      </v-card>
      <v-overlay
        :absolute="true"
        :value="progress_overlay"
      >
        <v-progress-circular
          :value="progress"
        >
          {{ progress }}
        </v-progress-circular>
      </v-overlay>
    </v-card>
  </v-dialog>
</template>

<script>
import saveAs from "file-saver";
import XLSX from "xlsx";
import { http_error } from "../js/http_error";
import sanitize_filename from "sanitize-filename";
import 'xlsx.utils.sanitize_sheet_name';
var pdfjs = require('pdfjs-dist/build/pdf.js');
var pdfjsWorker = ((typeof window !== "undefined"
  ? window
  : {}
).pdfjsWorker = require("pdfjs-dist/build/pdf.worker.js"));
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export default {
  name: 'viewer',
  mixins: [
    http_error
  ],
  props: {
    mode: String
  },
  data: function(){
    return {
      module_id: "68",
      dialog: false,
      fid: null,
      extension: null,
      file_name: null,
      instance: null,
      viewer_width: 0,
      viewer_height: 0,
      currentId: 0,
      total: 0,
      scale: null,
      text: "",
      // for json
      data: [],
      model_type: "field",
      search_source: "",
      search_translated: "",
      page: 1,
      options: {
        itemsPerPage: 10,
        sortBy: ['index'],
        sortDesc: [false]
      },
      progress: 0,
      progress_overlay: false,
      supported_extensions: [
        "txt",
        "docx",
        "xlsx",
        "pptx",
        "pdf",
        "ocr",
        "json"
      ],
      sheetHeight: 0,
      abort_controller: null
    };
  },
  created: function(){
    this.onResize();
  },
  methods: {
    open(fid, extension) {
      if(this.supported_extensions.indexOf(extension) === -1){
        /* 未サポートメッセージ */
        return;
      }
      if(this.instance){
        this.instance = null;
      }
      this.fid = fid;
      this.extension = extension;
      this.dialog = true;
      this.progress_overlay = true;
      this.progress = 0;
      this.abort_controller = new AbortController();
      this.$http.get(this.$store.getters.api_endpoint + "/" + this.get_prefix() + "file/" + fid + "/" + extension,
                     {
                       responseType: this.extension !== "json" ? "blob" : "json",
                       onDownloadProgress: function( progressEvent ) {
                         this.progress = parseInt( Math.round( ( progressEvent.loaded / progressEvent.total ) * 100 ));
                       }.bind(this),
                       signal: this.abort_controller.signal
                     })
        .then( response => {
          this.progress = 100;
          this.progress_overlay = false;
          this.abort_controller = null;
          var blob = new Blob([response.data], {
            type: response.data.type
          });

          //レスポンスヘッダからファイル名を取得します
          const contentDisposition = response.headers["content-disposition"];
          // ";"区切り文と、"="区切りキーバリューを解体していく
          var cdData = {};
          for(var cdStr of contentDisposition.split(";")){
            var cdKeyValue = cdStr.trim().split("=");
            if(cdKeyValue.length >= 2){
              cdData[cdKeyValue[0]] = cdKeyValue[1].replace(/^"/, "").replace(/"$/, "");
            }
          }
          if("filename*" in cdData){
            // キーfilename*が優先。こちらはURLデコードする
            this.file_name = cdData["filename*"].replace("UTF-8''", "");
            // デコードするとスペースが"+"になるのでスペースへ置換します
            this.file_name = decodeURIComponent(this.file_name).replace(/\+/g, " ");
          }else if("filename" in cdData){
            // キーfilenameはそのまま
            this.file_name = cdData["filename"];
          }
          try{
            this.onResize();
            if(this.extension === "txt"){
              const fileReader = new FileReader;
              fileReader.readAsText(blob);
              fileReader.onload = ()=> {
                this.text = fileReader.result;
              }
            }else if(this.extension === "json"){
              var data = [];
              if(("format" in response.data) && [3, 4].indexOf(response.data.format.version) >= 0){
                // format version 3, 4
                if("parameters" in response.data.metadata){
                  this.model_type = response.data.metadata.parameters.model_type;
                }
                for(const [no, top_data] of response.data.contents.entries()){
                  for(const [sub_no, sub_data] of top_data.contents.entries()){
                    for(const [sub_sub_no, sub_sub_data] of sub_data.contents.entries()){
                      var comment = sub_sub_data.comment;
                      if(typeof comment === "string"){
                        try{
                           comment = JSON.parse(comment);
                         }catch(e){
                           comment = {};
                         }
                      }
                      var indexes = [(parseInt(no) + 1)];
                      if(top_data.contents.length > 1){
                        indexes.push(parseInt(sub_no) + 1);
                      }
                      if(sub_data.contents.length > 1){
                        indexes.push(parseInt(sub_sub_no) + 1);
                      }
                      var d = {
                        index: indexes,
                        source: sub_sub_data.source.sentence,
                        translated: sub_sub_data.target.sentence,
                        count: sub_sub_data.cnt
                      };
                      if("scores" in sub_sub_data){
                        if("machine_translate" in sub_sub_data.scores
                          &&['develop', 'in-company'].filter(option => (this.$store.getters.enable_options.indexOf(option) >= 0)).length > 0){
                          d.score_machine_translate = sub_sub_data.scores.machine_translate;
                        }
                        if("custom_model" in sub_sub_data.scores){
                          d.score_custom_model = sub_sub_data.scores.custom_model;
                        }
                      }
                      data.push(d);
                    }
                  }
                }
              }
              this.data = data;
            }else if(['pdf', 'ocr'].indexOf(this.extension) >= 0){
              this.pdf_draw(blob);
            }else{
              this.draw(blob);
            }
          }catch(e){
            if(this.$store.getters.debug){
              console.error(e);
            }
            this.$store.commit("setNotification", {
              type: "error",
              message: {
                eid: "E1" + this.module_id + "311" + ":00000001",
                param: {}
              }
            });
          }
          this.createSheetHeight();
        })
        .catch(error => {
          this.progress_overlay = false;
          if(this.abort_controller !== null){
            this.abort_controller.abort();
          }
          this.abort_controller = null;
          if(this.http_error(error)){
            this.$store.commit("setNotification", {
              type: "error",
              message: error.response.data
            });
          }
        });
      window.addEventListener('resize', this.onResize);
    },
    draw: function(blob){
      switch(this.extension){
        case 'docx':
          this.instance = new DocxJS();
          break;
        case 'xlsx':
          this.instance = new CellJS();
          break;
        case 'pptx':
          this.instance = new SlideJS();
          break;
        default:
          return;
      }
      /** parse API
       *  structure : parse(file, successCallbackFn, errorCallbackFn) **/
      this.instance.parse(
          blob,
          function () {
              var kukuNode = this.$refs.viewerContents;
              if(!kukuNode){
                return;
              }
              /** render API
               *  structure : render(element, callbackFn, pageId) **/
              this.instance.render(kukuNode, function () {
                /*** After Renderer Logic ***/
                if(this.extension === 'xlsx') {
                  this.currentId = this.instance.getCurrentId() - 1;
                  var fileInfo = this.instance.getFileInfo();
                  if(fileInfo.sheetNames) {
                    this.total = this.instance.getFileInfo().sheetNames.length - 1;
                  }else{
                    this.total = 1;
                  }
                }else if(this.extension === 'pptx') {
                  this.currentId = this.instance.getCurrentId();
                  this.total = this.instance.getFileInfo().total;
                }
              }.bind(this));
          }.bind(this),
          function () {
            if(this.$store.getters.debug){
              console.log('document js viewer parsing error');
            }
            this.$store.commit("setNotification", {
              type: "error",
              message: {
                eid: "E1" + this.module_id + "311" + ":00000002",
                param: {}
              }
            });
          });
    },
    pdf_draw: function(blob){
      new Promise(function(resolve) {
        var reader = new FileReader();

        reader.onloadend = function() {
          resolve(reader.result);
        };

        reader.readAsArrayBuffer(blob);
      })
      .then(function(array) {
        if(this.$store.getters.debug){
          console.log("Array contains", array.byteLength, "bytes.");
        }

        return pdfjs.getDocument({
          data: array,
          cMapUrl: '/cmaps/',
          cMapPacked: true,
        }).promise;
      }.bind(this))
      .then(function(pdf) {
        if(this.$store.getters.debug){
          console.log('PDF loaded');
        }
        this.instance = pdf;
        this.currentId = 0;
        this.total = pdf.numPages;
        this.scale = null;
        this.pdf_page();
      }.bind(this))
      .catch(function(error){
        if(this.$store.getters.debug){
          console.log('pdf-js parsing error.: ' + error);
        }
        this.$store.commit("setNotification", {
          type: "error",
          message: {
            eid: "E1" + this.module_id + "311" + ":00000003",
            param: {}
          }
        });
      }.bind(this));
    },
    pdf_page: function(){
      if(this.instance == null){
        return;
      }
      this.instance.getPage(this.currentId + 1)
      .then(function(page) {
        if(this.$store.getters.debug){
          console.log('Page loaded');
        }

        var viewport;
        if(this.scale === null){
          viewport = page.getViewport({scale: 1});
          this.scale = this.viewer_width / viewport.width;
        }
        viewport = page.getViewport({scale: this.scale});

        // Prepare canvas using PDF page dimensions
        var canvas = this.$refs.pdf_canvas;
        var context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        // Render PDF page into canvas context
        var renderContext = {
          canvasContext: context,
          viewport: viewport
        };
        return page.render(renderContext);
      }.bind(this))
      .then(function () {
        if(this.$store.getters.debug){
          console.log('Page rendered');
        }
      }.bind(this))
      .catch(function(error){
        //if(this.$store.getters.debug){
          console.log('pdf-js parsing error.: ' + error);
        //}
        this.$store.commit("setNotification", {
          type: "error",
          message: {
            eid: "E1" + this.module_id + "311" + ":00000004",
            param: {}
          }
        });
      }.bind(this));

    },
    cancel() {
      if(this.abort_controller !== null){
        this.abort_controller.abort();
      }
      this.abort_controller = null;
      if(['pdf', 'ocr'].indexOf(this.extension) >= 0){
        var canvas = this.$refs.pdf_canvas;
        var context = canvas.getContext('2d');
        context.clearRect(0, 0, canvas.width, canvas.height);
      }else{
        this.remove_element();
      }
      this.dialog = false;
      this.fid = null;
      this.extension = null;
      this.file_name = null;
      this.currentId = 0;
      this.total = 0;
      this.scale = null;
      this.data = [];
      this.model_type = "field",
      this.search_source = "",
      this.search_translated = "",
      this.page = 1;
      this.options.itemsPerPage = 10;
      if(this.instance){
        this.instance = null;
      }
      this.viewer_width = 'auto';
      window.removeEventListener('resize', this.onResize);
      this.remove_range();
    },
    remove_element() {
      const kukuNode = this.$refs.viewerContents;
      if(!kukuNode){
        return;
      }
      // pdfを表示させるためにすでに表示している要素がある場合削除する
      const fileNode = kukuNode.querySelectorAll('div');
      if(fileNode.length){
        while (kukuNode.firstChild) kukuNode.removeChild(kukuNode.firstChild);
      }
    },
    prev: function(){
      if(this.instance) {
        if(this.extension === 'docx') {
          if(this.$store.getters.debug){
            console.log('no support prev page');
          }
        }else if(['pdf', 'ocr'].indexOf(this.extension) >= 0) {
          this.currentId--;
          this.pdf_page();
          this.reset_scroll();
        }else if(this.extension === 'xlsx') {
          this.instance.gotoPage(--this.currentId + 1);
        }else{
          this.instance.gotoPage(--this.currentId);
        }
        if(this.extension === 'pptx') {
          this.instance.setZoom(null);
        }
      }
    },
    next: function(){
      if(this.instance) {
        if(this.extension === 'docx') {
          if(this.$store.getters.debug){
            console.log('no support next page');
          }
        }else if(['pdf', 'ocr'].indexOf(this.extension) >= 0) {
          this.currentId++;
          this.pdf_page();
          this.reset_scroll();
        }else if(this.extension === 'xlsx') {
          this.instance.gotoPage(++this.currentId + 1);
        }else{
          this.instance.gotoPage(++this.currentId);
        }
        if(this.extension === 'pptx') {
          this.instance.setZoom(null);
        }
      }
    },
    zoom_in: function(){
      if(this.instance) {
        if(['pdf', 'ocr'].indexOf(this.extension) >= 0) {
          this.scale *= 1.2;
          this.pdf_page();
        }else{
          /** getZoom API
           *  structure : getZoom() **/
          var currentZoom = this.instance.getZoom();

          /** setZoom API
           *  structure : setZoom(zoomVal) **/
          this.instance.setZoom(currentZoom + 0.5);
        }
      }
    },
    zoom_out: function(){
      if(this.instance) {
        if(['pdf', 'ocr'].indexOf(this.extension) >= 0) {
          this.scale /= 1.2;
          this.pdf_page();
        }else{
          var currentZoom = this.instance.getZoom();
          this.instance.setZoom(currentZoom - 0.5);
        }
      }
    },
    onResize: function(){
      var toolbar_height = 48;
      var copyButton_height = 52;
      if(this.$refs.toolbar){
        toolbar_height = this.$refs.toolbar.extensionHeight;
      }
      if(this.$refs.copyButton && this.$refs.copyButton.clientHeight > 0){
        copyButton_height = this.$refs.copyButton.clientHeight;
      }
      if(this.extension === "pptx"){
        this.viewer_width = 'auto';
      }
      else if(this.extension === "json"){
        this.createSheetHeight();
      }
      else if(this.extension !== "txt"){
        this.viewer_width = Math.floor(0.8 * window.innerWidth);
        this.viewer_height = Math.floor(0.8 * window.innerHeight) - 16 - toolbar_height;
      }else if(this.extension === "txt" && this.$refs.textarea){
        this.viewer_width = Math.floor(0.8 * window.innerWidth);
      }
      else if(this.extension === "txt"){
        this.viewer_height = Math.floor(0.8 * window.innerHeight) - 64 - 16 - copyButton_height;
      }
    },
    get_prefix: function(){
      if(this.mode === "user"){
        return "";
      }else{
        return this.mode + "/";
      }
    },
    copy_text(){
      navigator.clipboard.writeText(this.get_text);
      this.$store.commit("setNotification", {
        type: "success",
        message: {
          eid: "E1" + this.module_id + "006" + ":00000000",
          param: {}
        }
      });
    },
    remove_range(){
      const selection = window.getSelection();
      if(selection.toString()){
        const range = selection.getRangeAt(0);
        selection.removeAllRanges(range);
      }
    },
    reset_scroll(){
      this.$refs.pdf_canvas.parentNode.scrollTop = 0;
      this.$refs.pdf_canvas.parentNode.scrollLeft = 0;
    },
    // sheet内の高さを設定
    createSheetHeight() {
      const $header = document.getElementById('header-Viewer');
      const headerHeight = $header.clientHeight;
      this.sheetHeight = window.innerHeight * 0.8 - headerHeight;
    },
    get_excel: function(){
      // データー変換
      var excel_data = [];
      for(var data of this.data){
        var row = [
          data.index,
          data.source,
          data.translated,
          data.count
        ];
        if(["t-3mt"].indexOf(this.$store.getters.domain) >= 0
          && this.model_type === "model"
          && "score_custom_model" in data){
          row.push(data.score_custom_model);
        }
        excel_data.push(row);
      }
      excel_data.sort(function(a, b) {
        if(Array.isArray(a[0]) && Array.isArray(b[0])){
          for(var i=0; i<Math.min(a[0].length, b[0].length); i++){
            if(a[0][i] !== b[0][i]){
              return a[0][i] - b[0][i];
            }
          }
          return 0;
        }else{
          return a[0] - b[0];
        }
      })
      for(var row of excel_data){
        if(Array.isArray(row[0])){
          row[0] = row[0].join("-");
        }
      }
      var headers = [
        "No.",
        this.$i18n.t("message.common.source"),
        this.$i18n.t("message.viewer.translated"),
        this.$i18n.t("message.viewer.count")
      ];
      if(["t-3mt"].indexOf(this.$store.getters.domain) >= 0
        && this.model_type === "model"){
        headers.push(this.$i18n.t("message.viewer.custom_model_similarity"));
      }
      excel_data.unshift(headers);

      // 以降はExcel処理
      var wb = XLSX.utils.book_new();

      if(this.$store.getters.debug){
        console.log(excel_data);
      }
      var ws_name = (this.file_name + "(" + this.fid + ")").substring(0, 31);
      var ws = XLSX.utils.aoa_to_sheet(excel_data);
      /* Add the worksheet to the workbook */
      XLSX.utils.book_append_sheet(wb, ws, XLSX.utils.sanitize_sheet_name(ws_name));

      /* bookType can be any supported output type */
      var wopts = { bookType:'xlsx', bookSST:false, type:'binary' };
      var wbout = XLSX.write(wb,wopts);
      function s2ab(s) {
        var buf = new ArrayBuffer(s.length);
        var view = new Uint8Array(buf);
        for (var i=0; i!=s.length; ++i) view[i] = s.charCodeAt(i) & 0xFF;
        return buf;
      }
      /* the saveAs call downloads a file on the local machine */
      saveAs(new Blob([s2ab(wbout)],{type:"application/octet-stream"}),
             sanitize_filename(this.file_name + ".xlsx"));
    }
  },
  computed: {
    get_text: function(){
      return this.text;
    },
    headers: function() {
      var headers = [
        {
          text: "No.",
          value: 'index',
          sort: (a, b) => {
            if(Array.isArray(a) && Array.isArray(b)){
              for(var i=0; i<Math.min(a.length, b.length); i++){
                if(a[i] !== b[i]){
                  return a[i] - b[i];
                }
              }
              return 0;
            }else{
              return a - b;
            }
          }
        },
        {
          text: this.$i18n.t("message.common.source"),
          value: 'source',
          filter: value => {
            if(this.search_source === "" || this.search_source === null){
              return true;
            }
            return value.toLowerCase().indexOf(this.search_source.toLowerCase()) !== -1;
          }
        },
        {
          text: this.$i18n.t("message.viewer.translated"),
          value: 'translated',
          filter: value => {
            if(this.search_translated === "" || this.search_translated === null){
              return true;
            }
            return value.toLowerCase().indexOf(this.search_translated.toLowerCase()) !== -1;
          }
        },
        {
          text: this.$i18n.t("message.viewer.count"),
          value: 'count',
          align: 'right'
        }
      ];
      if(['develop', 'in-company'].filter(option => (this.$store.getters.enable_options.indexOf(option) >= 0)).length > 0){
        headers.push(
          {
            text: this.$i18n.t("message.viewer.machine_translate_score"),
            value: 'score_machine_translate',
            align: 'right'
          }
        );
      }
      if(["t-3mt"].indexOf(this.$store.getters.domain) >= 0
        && this.model_type === "model"){
        headers.push(
          {
            text: this.$i18n.t("message.viewer.custom_model_similarity"),
            value: 'score_custom_model',
            align: 'right'
          }
        );
      }
      return headers;
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  }
}
</script>
<style>
.dialog-contents {
  overflow-x: hidden !important;
  overflow-y: hidden !important;
}
.pre-line {
  white-space: pre-line;
  overflow-x: hidden;
  overflow-y: scroll;
}
.no-radius {
  border-radius: 0px !important;
}
.canvas-contents {
  overflow-x: scroll;
  overflow-y: scroll;
}
.bidi {
  unicode-bidi: plaintext;
}
div.v-toolbar__extension {
  display: block;
  background-color: #FFF;
  padding: 0;
  padding-top: 8px;
}
.search-area {
  border-radius: 0 !important;
  box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px -2px rgba(0, 0, 0, 0.12);
}
.v-data-table-header > tr > th {
  padding: 24px 16px 12px 16px;
}
.viewer__header {
  position: relative !important;
}
</style>
